import React, { useContext } from "react";
import Recaptcha from "react-google-recaptcha";
import cx from "classnames";

import useContactForm from "../../hooks/useContactForm";
import { config } from "../../config";
import { CustomI18nextContext } from "../../contexts/CustomI18nextContext";

import "./contactForm.scss";

const ContactForm = (props) => {
  const { t, siteCountry } = useContext(CustomI18nextContext);
  const { formName, submitForm, handleRecaptcha, recaptchaResponse } = props;
  let recaptchaRef = React.createRef();

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    dirty,
    touched,
  } = useContactForm(siteCountry, submitForm);

  return (
    <form
      method="post"
      data-netlify="true"
      data-netlify-recaptcha="true"
      data-netlify-honeypot="bot-field"
      name={formName}
      onSubmit={handleSubmit}
    >
      <input type="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value={formName} />

      <div className="form-group ">
        <label htmlFor="inputFullName">{t("form.contactForm.fullName")}</label>
        <input
          data-testid="inp_fullname"
          name="fullname"
          type="text"
          className={cx("form-control", {
            invalid: errors.fullname && touched.fullname,
          })}
          id="inputFullName"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.fullname}
        />
        {errors.fullname && touched.fullname && (
          <span className="error">{errors.fullname}</span>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="inputEmailAddress">
          {t("form.contactForm.emailAddress")}
        </label>
        <input
          data-testid="inp_email"
          name="email"
          type="email"
          className={cx("form-control", {
            invalid: errors.email && touched.email,
          })}
          id="inputEmailAddress"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
        />
        {errors.email && touched.email && (
          <span className="error">{errors.email}</span>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="inputPhoneNumber">
          {t("form.contactForm.phoneNumber")}
        </label>
        <input
          data-testid="inp_phone"
          name="phone"
          type="tel"
          className={cx("form-control", {
            invalid: errors.phone && touched.phone,
          })}
          id="inputPhoneNumber"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.phone}
        />
        {errors.phone && touched.phone && (
          <span className="error">{errors.phone}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="inputAbout">
          {t("form.contactForm.about.howCanWeHelpYou")}
        </label>
        <select
          className={cx("form-control form-select", {
            invalid: errors.about && touched.about,
          })}
          data-testid="slc_about"
          name="about"
          id="inputAbout"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.about}
        >
          <option
            value=""
            aria-label="-- Select --"
            label="-- Select --"
            disabled
          />
          <option
            value="information"
            aria-label={t("form.contactForm.about.information")}
            label={t("form.contactForm.about.information")}
          />
          <option
            value="complaint"
            aria-label={t("form.contactForm.about.complaint")}
            label={t("form.contactForm.about.complaint")}
          />
          <option
            value="suggestion"
            aria-label={t("form.contactForm.about.suggestion")}
            label={t("form.contactForm.about.suggestion")}
          />
        </select>
        {errors.about && touched.about && (
          <span className="error">{errors.about}</span>
        )}
      </div>

      <div
        className={cx("form-group", {
          "d-none": values.about !== "information",
        })}
      >
        <label htmlFor="inputInformationTopic">
          {t("form.contactForm.informationTopic.whatIssue")}
        </label>
        <select
          className={cx("form-control form-select", {
            invalid: errors.informationTopic && touched.informationTopic,
          })}
          data-testid="slc_information-topic"
          name="informationTopic"
          id="inputInformationTopic"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.informationTopic}
        >
          <option
            value=""
            aria-label="-- Select --"
            label="-- Select --"
            disabled
          />
          {t("form.contactForm.informationTopic.list", {
            returnObjects: true,
          }).map(({ value, label }, i) => (
            <option
              key={`information-topic-${i}`}
              value={value}
              aria-label={label}
              label={label}
            />
          ))}
        </select>
        {errors.informationTopic && touched.informationTopic && (
          <span className="error">{errors.informationTopic}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="inputMessage">
          {t("form.contactForm.yourMessage")}
        </label>
        <textarea
          data-testid="txt_message"
          name="message"
          id="inputMessage"
          className={cx("form-control", {
            invalid: errors.message && touched.message,
          })}
          rows="5"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.message}
        ></textarea>
        {errors.message && touched.message && (
          <span className="error">{errors.message}</span>
        )}
      </div>

      <Recaptcha
        ref={recaptchaRef}
        sitekey={config.reCaptcha_key}
        onChange={handleRecaptcha}
      />

      <button
        data-testid="btn_submit"
        type="submit"
        className={"btn btn-primary mt-3"}
        disabled={!(dirty && isValid) || !recaptchaResponse}
      >
        {t("form.contactForm.btnSendMessage")}
      </button>
    </form>
  );
};

export default ContactForm;
